import React, { useEffect, useMemo, useState } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import DocumentationSidebar from "../../../../Layout/DocumentationSidebar";
import TopMenu from "../../TopMenu";
import TableDocument from "../../TableDocument";
import FilterModal from "../../../../Components/Common/FilterModal";
import { get } from "../../../../utils/api";

const API_BASE = "/scope3/eolTreatmentOfSoldProducts";

const MASTER_PRODUCTS_PATH =
  "/master/product/productsList?page=1&limit=1000&sortField=createdAt&sortOrder=desc";

const resolveProductMasterDoc = (item, productById) => {
  const raw = item?.productMaster;
  if (raw && typeof raw === "object") {
    const hasDetails =
      raw.productId != null ||
      raw.productName != null ||
      raw.disposalMethod != null;
    if (hasDetails) return raw;
    const id =
      raw._id != null
        ? String(raw._id)
        : raw.id != null
          ? String(raw.id)
          : "";
    if (id && productById.has(id)) return productById.get(id);
    return raw;
  }
  if (raw != null && String(raw).trim() !== "") {
    const id = String(raw).trim();
    if (productById.has(id)) return productById.get(id);
  }
  const pmid = item?.productMasterId;
  if (pmid != null && String(pmid).trim() !== "") {
    const k = String(pmid).trim();
    if (productById.has(k)) return productById.get(k);
  }
  return null;
};

const formatEolMonthForTable = (value) => {
  if (value == null || value === "") return "";
  const monthShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (typeof value === "object" && value.month != null && value.year != null) {
    const m = Number(value.month);
    return `${monthShort[m - 1] || value.month} ${value.year}`;
  }
  if (typeof value === "string") {
    const ymMatch = value.match(/^(\d{4})-(\d{1,2})$/);
    if (ymMatch) {
      const m = Number(ymMatch[2]);
      return `${monthShort[m - 1] || ymMatch[2]} ${ymMatch[1]}`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return `${monthShort[parsed.getMonth()]} ${parsed.getFullYear()}`;
    }
  }
  return String(value);
};

const mapEolDocumentRow = (item, productById) => {
  const pm = resolveProductMasterDoc(item, productById);
  return {
    ...item,
    eolMonth: formatEolMonthForTable(item?.eolMonth),
    productId: pm?.productId ?? item?.productId ?? "",
    productName: pm?.productName ?? item?.productName ?? "",
    endOfLifeTreatmentMethod:
      pm?.disposalMethod ?? item?.endOfLifeTreatmentMethod ?? "",
  };
};

const EOLTreatmentOfSoldProductsDocuments = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const columnWidth =
    "12% 15% 13% 18% 17% 15% 10%";

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [url, setUrl] = useState("road");
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [masterProducts, setMasterProducts] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });

  const [filter, setFilter] = useState({
    productId: [],
    productName: [],
    endOfLifeTreatmentMethod: [],
  });

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
  };

  const handleToggle = (option, nextUrl) => {
    setSelectedOption(option);
    setUrl(nextUrl);
  };

  let filtersToApply = {};

  const productById = useMemo(() => {
    const m = new Map();
    for (const p of masterProducts) {
      if (p?._id != null) m.set(String(p._id), p);
      const pid = p?.productId != null ? String(p.productId).trim() : "";
      if (pid && !m.has(pid)) m.set(pid, p);
    }
    return m;
  }, [masterProducts]);

  const data = useMemo(
    () => rawRows.map((row) => mapEolDocumentRow(row, productById)),
    [rawRows, productById]
  );

  const fetchData = async (
    filters = isFiltersApplied ? filtersToApply : {}
  ) => {
    try {
      const queryParams = new URLSearchParams({
        ...filters,
        page,
        facilityList: selectedFacility.join(","),
        limit: 10,
        search: searchQuery,
      }).toString();

      const response = await get(
        `${API_BASE}/documents?${queryParams}&startYear=${fromDate.year}&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${toDate.month}`
      );
      setRawRows(Array.isArray(response.data.data) ? response.data.data : []);
      setTotalPages(response.data.totalPages);
      setPage(response.data.currentPage);
    } catch (error) {
      console.error("Error fetching EOL treatment documents:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, url, fromDate, toDate, selectedFacility, searchQuery]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await get(MASTER_PRODUCTS_PATH);
        setMasterProducts(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (e) {
        console.error("Error loading product master for EOL documents:", e);
        setMasterProducts([]);
      }
    };
    loadProducts();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const heading = [
    { title: "Date", key: "createdAt" },
    { title: "Production Month", key: "eolMonth" },
    { title: "Product Id", key: "productId" },
    { title: "Product Name", key: "productName" },
    { title: "EOL Treatment Method", key: "endOfLifeTreatmentMethod" },
    { title: "Number of Units Sold", key: "numberOfUnitsSold" },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "createdAt", value: [] },
    { title: "Production Month", type: "month", key: "eolMonth", value: [] },
    {
      title: "Product Id",
      type: "option",
      key: "productId",
      value: filter.productId || [],
    },
    {
      title: "Product Name",
      type: "option",
      key: "productName",
      value: filter.productName || [],
    },
    {
      title: "EOL Treatment Method",
      type: "option",
      key: "endOfLifeTreatmentMethod",
      value: filter.endOfLifeTreatmentMethod || [],
    },
  ];

  const toggleData = [];

  return (
    <MainDashboardLayout sidebar={<DocumentationSidebar />}>
      <TopMenu
        {...{
          setDrawerFilterOpen: setIsFilterModalOpen,
          selectedMonth,
          setSelectedMonth,
          selectedYear,
          setSelectedYear,
          selectedOption,
          handleToggle,
          filterApplied: isFiltersApplied,
          toggleData,
          fromDate,
          setFromDate,
          toDate,
          setToDate,
          selectedFacility,
          setSelectedFacility,
          searchQuery,
          setSearchQuery,
          fetchData,
        }}
        heading="EOL Treatment of Sold Products"
      />

      <TableDocument
        data={data}
        totalPages={totalPages}
        page={page}
        handleChangePage={handleChangePage}
        headers={heading}
        columnWidth={columnWidth}
      />

      {/* <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      /> */}
    </MainDashboardLayout>
  );
};

export default EOLTreatmentOfSoldProductsDocuments;
